import React, { useState } from "react";
import { toast } from "sonner";
import { TriggerNode } from "../../../../views/convoauto-pages/bot-studio/types";
import useJouneyReq from "../../../hook/bot-req";
import { NodeMenu } from "../../../shared/trigger-shared/node-menu";
import { RightDot } from "../../../shared/action-core";
import { DeleteModal } from "../../../shared/trigger-shared/delete-modal";
import { LeadFormModal } from "./lead-modal";

interface NewLeadTriggerCardProps {
  node: TriggerNode;
  onChange: (n: TriggerNode) => void;
  onDelete: () => void;
  onReset: () => void;
  journeyId?: string;
}

interface LeadFormData {
  phone: string;
  first_name: string;
  last_name: string;
  email: string;
  source: string;
}

interface SubmitLeadResponse {
  workflow_id: string;
  lead_correlation_id: string;
  status: string;
  message?: string;
}

const LEAD_SOURCE_OPTIONS = ["Manual"];
const LEAD_CONTACTS_STORAGE_KEY = "journey_lead_contacts";

const normalizeLeadSource = (source: unknown) => {
  if (typeof source !== "string") return "Manual";

  const legacySourceMap: Record<string, string> = {
    manual: "Manual",
    website_form: "Website Form",
    landing_page: "Website Form",
    referral: "Referral",
    api: "Manual",
    linkedin: "LinkedIn",
  };

  return legacySourceMap[source] ?? source;
};

const buildLeadPayload = (lead: LeadFormData): LeadFormData => ({
  phone: lead.phone.trim(),
  first_name: lead.first_name.trim(),
  last_name: lead.last_name.trim(),
  email: lead.email.trim(),
  source: normalizeLeadSource(lead.source),
});

const saveLeadContactRecord = (lead: LeadFormData, response: SubmitLeadResponse) => {
  if (!response?.lead_correlation_id) return;

  const record = {
    ...lead,
    workflow_id: response.workflow_id,
    lead_correlation_id: response.lead_correlation_id,
    submitted_at: new Date().toISOString(),
  };

  try {
    const saved = localStorage.getItem(LEAD_CONTACTS_STORAGE_KEY);
    const contacts = saved ? JSON.parse(saved) : [];
    const nextContacts = [
      record,
      ...(Array.isArray(contacts)
        ? contacts.filter((item: any) => item?.lead_correlation_id !== response.lead_correlation_id)
        : []),
    ];

    localStorage.setItem(LEAD_CONTACTS_STORAGE_KEY, JSON.stringify(nextContacts));
    localStorage.setItem(`lead_correlation_${response.lead_correlation_id}`, JSON.stringify(record));
  } catch {
    localStorage.setItem(`lead_correlation_${response.lead_correlation_id}`, JSON.stringify(record));
  }
};

export const NewLeadTriggerCard: React.FC<NewLeadTriggerCardProps> = ({
  node,
  onChange,
  onDelete,
  onReset,
  journeyId,
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showLeadForm, setShowLeadForm] = useState(false);

  const update = (patch: Partial<TriggerNode>) => {
    if (node && onChange) {
      onChange({ ...node, ...patch });
    }
  };

  const handleLeadSuccess = (result: SubmitLeadResponse, lead?: LeadFormData) => {
    const currentConfig = node.config || {};
    update({
      config: {
        ...currentConfig,
        last_test_submitted: new Date().toISOString(),
        last_test_lead_id: result.lead_correlation_id,
        last_test_lead: lead,
      }
    });
  };

  const sourceValue = node?.source && typeof node.source === 'string'
    ? normalizeLeadSource(node.source)
    : "Manual";

  return (
    <>
      <div className="relative">
        <div className="absolute -top-7 left-0">
          <span className="bg-gray-800 text-white text-xs px-3 py-1 rounded-full">
            Bot Starting Point
          </span>
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-4 w-[270px]">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-[#7B7B7B] font-semibold dark:text-gray-500 uppercase tracking-wide">
              Trigger
            </span>
            <NodeMenu
              onDelete={() => setShowDeleteModal(true)}
              onReset={onReset}
            />
          </div>

          <p className="text-indigo-600 font-semibold text-sm mb-0.5">New Lead</p>
          <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mb-4">
            Triggered when a new lead is submitted via form or API
          </p>

          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Default Source</label>
          <div className="relative mb-4">
            <select
              value={sourceValue}
              onChange={(e) => update({ source: e.target.value })}
              className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm appearance-none bg-white dark:bg-gray-900 pr-8 focus:outline-none focus:ring-1 focus:ring-indigo-300"
            >
              {LEAD_SOURCE_OPTIONS.map((source) => (
                <option key={source} value={source}>{source}</option>
              ))}
            </select>
            <div className="absolute right-2 top-2.5 pointer-events-none">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>
          </div>

          <div className="space-y-2">
            <button
              type="button"
              onClick={() => setShowLeadForm(true)}
              className="w-full py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              Submit Lead
            </button>
          </div>

          <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mt-2 text-center">
            Submit a lead manually to test the flow
          </p>

          {/* Connection dot */}
          <RightDot />
        </div>
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <DeleteModal
          title="Delete trigger?"
          description="This will remove the trigger and any connected flow from your bot journey. This action cannot be undone."
          confirmLabel="Delete Trigger"
          onConfirm={() => {
            onDelete();
            setShowDeleteModal(false);
          }}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}

      {/* Lead Form Modal */}
      <LeadFormModal
        isOpen={showLeadForm}
        onClose={() => setShowLeadForm(false)}
        journeyId={journeyId}
        onSuccess={handleLeadSuccess}
      />
    </>
  );
};